"use strict";
/**
 * Permission Gates — declarative UI gating from the current permission set.
 *
 * ONE place maps DOM controls to the permission predicate that governs them.
 * `applyPermissionUi()` walks the table and applies each gate; it is invoked
 * from the central state-change handler (services/state-handler.ts) whenever
 * permissions or the active page change, so gates re-evaluate automatically as
 * the user logs in, reloads, or navigates.
 *
 * To gate a new control: add one row to `GATES` (and, for a brand-new
 * capability, a helper in permissions.ts + a feature flag server-side). No
 * other file should special-case permissions in the DOM.
 *
 * Gating is cosmetic — the server enforces every cap/feature independently
 * (see PERMISSION_LEVELS_PLAN.md). These gates only avoid doomed clicks and
 * hide unusable controls.
 *
 * @module gates
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyGates = applyGates;
exports.updatePermissionBadge = updatePermissionBadge;
exports.updateBetCapHint = updateBetCapHint;
exports.applyPermissionUi = applyPermissionUi;
const state_1 = require("./state");
const capabilities_1 = require("./venues/capabilities");
const permissions_1 = require("./permissions");
/** CSS class that force-hides a gated control (see styles.css). */
const GATED_HIDDEN_CLASS = "permission-gated-hidden";
/**
 * The gate registry. Order is irrelevant; each row is independent.
 *
 * Arb-monitor entry points are UI-only: the server keeps the route behind the
 * elevated (Y-session) role regardless (PERMISSION_LEVELS_PLAN.md §6).
 */
const GATES = [
    // Arbitrage-monitor surfaces — owner tier only, purely cosmetic.
    { selector: "#open-monitor-status-btn", allow: permissions_1.canArbMonitor },
    { selector: "#open-pair-builder-btn", allow: permissions_1.canArbMonitor },
    // Manual limit-price control (order overlay + its settings toggle row).
    // No-op on today's tiers (all allow it) but wired so the gate stays honest.
    { selector: "#manual-price-container", allow: permissions_1.canManualPrice },
    { selector: "#manual-price-setting-group", allow: permissions_1.canManualPrice },
    // "Sell opposite" (XOP) close-opposite-on-buy control — owner-tier only.
    // Behaviour is also server-enforced (auth/levels.ts allowCloseOpposite);
    // hiding here keeps the client from sending a close the server would zero.
    { selector: "#close-opposite-toggle", allow: permissions_1.canCloseOpposite },
    // T0/Delayed pricing toggle — trusted tiers only (`delayed-price` feature).
    // Server-enforced (trade + PM-US routes force useDelayedPrice off without the
    // flag); the order-creation paths also AND useDelayedPrice with
    // permsAllowDelayedPrice, and hiding here keeps the toggle away entirely.
    { selector: "#toggle-delayed-price-btn", allow: permissions_1.canDelayedPrice },
    // Two-click (2C) confirmation toggle — trusted tiers only (`confirm-toggle`
    // feature, level 4+). UI-only: the 2-click guard has no server side, so this
    // gate is purely cosmetic. Tiers without the flag keep the guard on (the
    // confirm flag defaults on) and can't reach the toggle to relax it.
    { selector: "#toggle-confirm-btn", allow: permissions_1.canToggleConfirm },
    // Settings-modal network diagnostic — owner only. Hides the whole row so
    // the buttons and the result block go together; leaving the output behind
    // after a tier change would strand a stale verdict on screen.
    { selector: "#connection-check-row", allow: permissions_1.canConnectionCheck },
    // Win-probability page entry — owner only. Unlike the other owner-only
    // gates this one has a server side (`requireFeature` on /win-prob), so
    // hiding the button is convenience rather than the control itself.
    { selector: "#open-win-prob-btn", allow: permissions_1.canWinProb },
    // Novig venue pill — owner tier only while the venue is built out
    // (`novig-venue` feature). Purely cosmetic in both directions: the server
    // mounts the /novig router only when it holds Novig credentials, so a
    // deploy without them serves nothing regardless of this gate.
    //
    // Hidden via .permission-gated-hidden, NOT the `hidden` attribute the
    // markup uses for legacy Polymarket — see pill-selector.ts, which has to
    // know about both to keep a gated pill off the arrow-key path.
    { selector: "#data-source [data-value='novig']", allow: permissions_1.canNovigVenue },
    // Novig as a fan-out target, in the parallel and additional pickers — same
    // feature; the server's order route checks it too.
    { selector: "#parallel-market-venue-novig, #am-venue-btn-novig", allow: permissions_1.canNovigVenue },
    { selector: "#pnl-venue [data-value='novig']", allow: permissions_1.canNovigVenue },
    // MARK button (operator-invoked price sample) — trusted tiers only (level
    // 4+). UI-only: the endpoint is session-scoped to the caller's own click
    // ids, so this hides a control a lower tier would never arm rather than
    // guarding anything. The button's own `hidden` (nothing armed) is a
    // separate mechanism and does not collide with the gate class.
    { selector: "#price-sample-btn", allow: permissions_1.canPriceSample },
    // Monthly PnL Me/All and "view as" — owner only. The server ignores
    // scope=all and refuses viewAs from anyone else, so this only hides
    // controls that would change nothing.
    { selector: "#pnl-scope, #pnl-view-as", allow: permissions_1.canPnlAllUsers },
    // Additional-markets assign entry points — trusted tiers only (level 4+).
    // Server-enforced (order routes 403 a tagged additional order without the
    // capability); the fan-out is also suppressed client-side and the assign UI
    // hidden here. Covers the modal opener plus the baseball quick-assign
    // buttons (🏏/⚾/🥅) that pre-fill the same modal.
    {
        selector: "#open-additional-markets-btn, #bat-btn, #pitch-btn, #total-btn",
        allow: permissions_1.canAdditionalMarkets,
    },
    // Parallel-markets card (legs + Manage button) — trusted tiers only (level
    // 4+). Server-enforced (order routes 403 a tagged parallel order without the
    // capability); the fan-out is also suppressed client-side and the assign UI
    // hidden here.
    { selector: "#parallel-markets-card", allow: permissions_1.canParallelMarkets },
    // Buy-button gate — three conditions folded into one row (a second disable
    // gate on the same selector would fight this one: applyDisable sets
    // `disabled` unconditionally, so the later row would re-enable the button).
    //  1. A view-only venue can't be traded at all. This
    //     is venue state rather than permission state, and living in the
    //     permission table is a deliberate compromise: this table owns
    //     `disabled` on these buttons, so a second owner would fight it.
    //  2. $0-cap tier (level 0) can't bet at all — the server rejects every
    //     order, so disable the buy buttons entirely with a tier-specific title.
    //  3. Otherwise, a bet-cap backstop: the committed amount is clamped to the
    //     cap at entry (permissions.clampBetToCap), so this normally passes; it
    //     stays as a visible guard for any path that sets an over-cap amount.
    // The server 403 is the ultimate backstop the operator should never see.
    {
        selector: "#buy-option-a, #buy-option-b",
        effect: "disable",
        allow: () => (0, capabilities_1.canTradeVenue)((0, state_1.getState)().dataSource) &&
            (0, permissions_1.canPlaceBets)() &&
            (0, permissions_1.isWithinBetCap)((0, state_1.getState)().tradingSettings.dollarAmount),
        deniedTitle: () => (0, capabilities_1.tradingDisabledReason)((0, state_1.getState)().dataSource) ??
            ((0, permissions_1.canPlaceBets)()
                ? `Amount exceeds your ${(0, permissions_1.formatBetCap)()} per-order cap`
                : "Your account can't place bets"),
    },
    // Sell surfaces — trusted tiers only (4+), server-enforced. All four
    // layouts' S: buttons plus the sell-only settings controls.
    {
        selector: "#sell-option-a, #sell-option-b, #bb-sell-row, #bk-sell-row, #sc-sell-row, #sell-mode-setting-group, #fixed-sell-quantity-group",
        allow: permissions_1.canSellOrders,
    },
    // $0-cap backstop, for a hypothetical sells-with-$0-cap tier (today's are
    // all covered by the hide gate above), plus the venue check: view-only, or
    // buys-only (Novig). Stacking against the hide gate above is safe: hide and
    // disable use different mechanisms, unlike two disable gates. Gates ONLY on
    // canPlaceBets — sells are never bet-capped.
    {
        selector: "#sell-option-a, #sell-option-b",
        effect: "disable",
        allow: () => (0, capabilities_1.sellDisabledReason)((0, state_1.getState)().dataSource) === null && (0, permissions_1.canPlaceBets)(),
        deniedTitle: () => (0, capabilities_1.sellDisabledReason)((0, state_1.getState)().dataSource) ??
            "Your account can't place orders",
    },
];
/** Show/hide a single element via the dedicated gate class.
 *
 * The class survives only as long as nothing else rewrites the element's
 * `className` — gates re-run on permission/page/amount changes, not on every
 * render, so a control that assigns `className` un-gates itself until the next
 * one. Controls must use `classList` for their own classes. */
function applyHide(el, permitted) {
    el.classList.toggle(GATED_HIDDEN_CLASS, !permitted);
}
/** Enable/disable a single element and manage its denial tooltip. */
function applyDisable(el, permitted, gate) {
    const btn = el;
    btn.disabled = !permitted;
    btn.classList.toggle("permission-cap-blocked", !permitted);
    if (!permitted && gate.deniedTitle) {
        if (btn.dataset.gateTitleSaved === undefined) {
            btn.dataset.gateTitleSaved = btn.getAttribute("title") ?? "";
        }
        btn.setAttribute("title", gate.deniedTitle());
    }
    else if (btn.dataset.gateTitleSaved !== undefined) {
        // Restore whatever title the control had before we overrode it.
        const original = btn.dataset.gateTitleSaved;
        if (original)
            btn.setAttribute("title", original);
        else
            btn.removeAttribute("title");
        delete btn.dataset.gateTitleSaved;
    }
}
/** Apply every gate in the registry against the live DOM. */
function applyGates() {
    for (const gate of GATES) {
        const permitted = gate.allow();
        const els = document.querySelectorAll(gate.selector);
        els.forEach((el) => {
            if (gate.effect === "disable")
                applyDisable(el, permitted, gate);
            else
                applyHide(el, permitted);
        });
    }
}
/** Refresh the tier badge(s) from the current permission set.
 *
 * A badge may be wrapped in a `[data-permission-badge-row]` container (e.g.
 * the settings-modal row with a label); that wrapper is hidden/shown with the
 * badge so a stray label never shows without a value. */
function updatePermissionBadge() {
    const badges = document.querySelectorAll("[data-permission-badge]");
    const perms = (0, permissions_1.getPermissions)();
    const level = (0, permissions_1.currentLevel)();
    const show = !!perms && level !== null;
    badges.forEach((badge) => {
        const row = badge.closest("[data-permission-badge-row]");
        // No permission set yet (or fail-open null): hide the badge (and its
        // row wrapper) rather than show a misleading tier.
        if (!show) {
            badge.classList.add("hidden");
            badge.textContent = "";
            row?.classList.add("hidden");
            return;
        }
        badge.textContent = `Lvl ${level} · ${(0, permissions_1.formatBetCap)()}`;
        badge.classList.remove("hidden");
        row?.classList.remove("hidden");
    });
}
/** Refresh the per-order cap hint next to the dollar-amount control. */
function updateBetCapHint() {
    const hint = document.getElementById("bet-cap-hint");
    if (!hint)
        return;
    const cap = (0, permissions_1.maxBetUsd)();
    if (!Number.isFinite(cap)) {
        hint.classList.add("hidden");
        hint.textContent = "";
        return;
    }
    hint.textContent = `max ${(0, permissions_1.formatBetCap)()}`;
    hint.classList.remove("hidden");
}
/**
 * Single entry point: apply all permission-driven UI. Call after any state
 * change that could affect permissions, page, or the dollar amount.
 */
function applyPermissionUi() {
    applyGates();
    updatePermissionBadge();
    updateBetCapHint();
}
